import { useEffect, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Calendar, Hash, ArrowLeft } from 'lucide-react'
import ReactMarkdown, { type Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { ScrollReveal } from '@/components/shared/ScrollReveal'
import { Badge } from '@/components/ui/Badge'
import { blogPostBySlug, loadBlogContent, type BlogContent } from '@/content/blogs'

function formatDate(dateStr: string, lang: 'vi' | 'en'): string {
  const date = new Date(dateStr)
  return date.toLocaleDateString(lang === 'vi' ? 'vi-VN' : 'en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  })
}

const markdownComponents: Components = {
  h1({ node, ...props }) {
    void node
    return <h2 {...props} />
  },
  a({ node, href, ...props }) {
    void node
    const isExternal = href?.startsWith('http://') || href?.startsWith('https://')
    return (
      <a
        {...props}
        href={href}
        target={isExternal ? '_blank' : undefined}
        rel={isExternal ? 'noopener noreferrer' : undefined}
      />
    )
  },
  img({ node, alt, ...props }) {
    void node
    return <img {...props} alt={alt ?? ''} loading="lazy" />
  },
  table({ node, ...props }) {
    void node
    return (
      <div className="article-markdown__table-wrap">
        <table {...props} />
      </div>
    )
  },
}

function MarkdownContent({ content }: { content: string }) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={markdownComponents}
      skipHtml
    >
      {content}
    </ReactMarkdown>
  )
}

function splitAfterFirstBlock(content: string): [string, string] {
  const blocks = content.trim().split(/\n{2,}/)
  return [blocks[0] ?? '', blocks.slice(1).join('\n\n')]
}

export function BlogPost() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  
  const post = slug ? blogPostBySlug.get(slug) : undefined
  const id = post?.id
  const [loaded, setLoaded] = useState<{ id: string; content?: BlogContent; error?: boolean } | null>(null)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    if (!id) return
    const controller = new AbortController()
    loadBlogContent(id, controller.signal).then(content => {
      if (!controller.signal.aborted) setLoaded({ id, content })
    }).catch(() => {
      if (!controller.signal.aborted) setLoaded({ id, error: true })
    })
    return () => controller.abort()
  }, [id, attempt])

  if (!post) {
    return (
      <section className="py-24 lg:py-32 bg-vibe-black min-h-screen">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <h1 className="text-2xl text-text-heading mb-4">Post not found</h1>
          <Link to="/blog" className="text-accent-primary hover:underline">
            Back to all posts
          </Link>
        </div>
      </section>
    )
  }

  const body = loaded && loaded.id === id ? loaded.content : undefined
  const failed = loaded && loaded.id === id && loaded.error
  const rawContent = body ? (post.lang === 'en' ? body.contentEn : body.contentVi) : ''
  // Strip the leading # title line since it's shown in the header
  const content = rawContent.trimStart().replace(/^#\s+.+\n*/, '')
  const title = post.lang === 'en'
    ? post.titleEn
    : post.firstLineVi.replace(/^#\s+/, '').slice(0, 100)
  const [intro, rest] = splitAfterFirstBlock(content)

  return (
    <section className="py-24 lg:py-32 bg-vibe-black min-h-screen">
      <article className="max-w-2xl mx-auto px-6 lg:px-8" lang={post.lang === 'vi' ? 'vi' : 'en'}>
        <ScrollReveal>
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-text-muted hover:text-text-primary mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-small">{post.lang === 'vi' ? 'Quay lại' : 'Back'}</span>
          </button>
        </ScrollReveal>

        <ScrollReveal delay={0.1}>
          <header className="mb-10">
            <div className="flex items-center gap-3 mb-4 text-tiny text-text-muted">
              <span className="flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" />
                {formatDate(post.date, post.lang)}
              </span>
              <Badge variant="muted" className="text-[10px]">
                {post.lang.toUpperCase()}
              </Badge>
              {post.hashtags.length > 0 && (
                <span className="flex items-center gap-1">
                  <Hash className="w-3.5 h-3.5" />
                  {post.hashtags.join(', ')}
                </span>
              )}
            </div>

            <h1 className="text-2xl lg:text-3xl font-medium leading-tight tracking-tight text-balance text-text-heading">
              {title}
            </h1>
          </header>
        </ScrollReveal>

        {!body && (
          <div role="status" className="text-text-secondary text-small">
            {failed ? (
              <>
                <p>Could not load this article.</p>
                <button className="mt-3 text-accent-primary hover:underline" onClick={() => {
                  setLoaded(null)
                  setAttempt(value => value + 1)
                }}>Try again</button>
              </>
            ) : 'Loading article…'}
          </div>
        )}

        {body && <div className="article-markdown">
          <MarkdownContent content={intro} />
          {post.images.map((image, index) => (
            <motion.img
              key={image}
              src={image}
              alt=""
              role="presentation"
              loading="lazy"
              className="article-markdown__featured-image"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 + index * 0.1 }}
            />
          ))}
          {rest && <MarkdownContent content={rest} />}
        </div>}

        <ScrollReveal delay={0.3}>
          {post.hashtags.length > 0 && (
            <div className="mt-10 pt-6 border-t border-vibe-border">
              <div className="flex flex-wrap gap-2">
                {post.hashtags.map(tag => (
                  <Link key={tag} to={`/blog?tag=${tag}`}>
                    <Badge variant="muted">#{tag}</Badge>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </ScrollReveal>

        <ScrollReveal delay={0.4}>
          <div className="mt-10 pt-6 border-t border-vibe-border">
            <p className="text-text-muted text-small">
              {post.lang === 'vi' ? 'Đăng gốc trên' : 'Originally posted on'}{' '}
              <a 
                href="https://t.me/will_thoughts" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-accent-primary hover:underline"
              >
                @will_thoughts
              </a>
            </p>
          </div>
        </ScrollReveal>
      </article>
    </section>
  )
}
